import { useEffect, useMemo, useState } from 'react';
import type { Order, OrderServicerInfo } from '@/types/order';
import {
  fetchAdminTeamMembers,
  toAvailableServicerServiceTime,
  type TeamMember,
} from '@/api';
import { computeEqualDurationAllocations, listOrderAssignedStaff } from '@/lib/settleAllocation';
import type { OrderBillingValues } from '@/lib/orderBilling';
import { OrderBillingFields, useOrderBilling } from './OrderBillingFields';

const DEFAULT_AVATAR =
  'https://ui-avatars.com/api/?name=Member&background=e8f5e9&color=1b5e20';

function formatSplitHours(hours: number): string {
  const rounded = Math.round(hours * 100) / 100;
  return `${rounded.toFixed(2)} h`;
}

interface AssignOrderModalProps {
  order: Order | null;
  submitting: boolean;
  /** `assign` 走首次派单：只提交人员和总工时。 */
  mode?: 'assign' | 'billing';
  onClose: () => void;
  onConfirm: (
    operatorIds: string[] | undefined,
    billing: OrderBillingValues,
    staff: OrderServicerInfo[],
  ) => void;
}

export default function AssignOrderModal({
  order,
  submitting,
  mode = 'billing',
  onClose,
  onConfirm,
}: AssignOrderModalProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const billing = useOrderBilling(order);
  const isAssign = mode === 'assign';
  const canEditStaff = isAssign || order?.status === 'pending' || order?.status === 'in_progress';

  const selectedStaff = useMemo<OrderServicerInfo[]>(() => {
    if (!order) return [];
    if (!canEditStaff) return listOrderAssignedStaff(order);
    return selectedIds.map((id) => {
      const member = members.find((item) => item.id === id);
      const existing = order.servicers?.find((item) => String(item.operatorId) === id);
      return {
        operatorId: id,
        name: member?.name || existing?.name || `Staff #${id}`,
        avatar: member?.avatar || existing?.avatar,
        email: existing?.email,
        mobile: existing?.mobile || member?.phone,
        hourlyWage: existing?.hourlyWage ?? member?.hourlyWage,
      };
    });
  }, [canEditStaff, members, order, selectedIds]);

  const hourSplit = useMemo(
    () => computeEqualDurationAllocations(billing.duration, selectedStaff),
    [billing.duration, selectedStaff],
  );

  useEffect(() => {
    if (!order) {
      setSelectedIds([]);
      setMembers([]);
      setError('');
      return;
    }

    setSelectedIds(order.assignees.map(String));
    if (!isAssign && order.status !== 'pending' && order.status !== 'in_progress') {
      setMembers([]);
      setLoading(false);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    let cancelled = false;
    fetchAdminTeamMembers({
      orderId: order.id,
      city: order.city,
      serviceTime: toAvailableServicerServiceTime(order.service_time),
    })
      .then((list) => {
        if (!cancelled) setMembers(list);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load staff.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAssign, order]);

  if (!order) return null;

  const toggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[60]" onClick={onClose} />
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[60] bg-white rounded-lg border border-gray-200 max-w-md mx-auto max-h-[88vh] flex flex-col">
        <div className="p-3 flex-shrink-0">
          <div className="text-gray-800 font-bold text-base mb-1">{isAssign ? 'Assign' : 'Price & Hours'}</div>
          <p className="text-xs text-gray-400">
            {isAssign
              ? 'Select staff and set the total hours. The hours are split equally across the people you assign.'
              : canEditStaff
              ? 'Update the price, work hours, and assigned staff. Selected staff share the total hours equally.'
              : 'Service is already completed. You can update the work hours and order price. Assigned staff keep an equal share of the total hours.'}
          </p>
        </div>

        <div className="px-3 pb-3 overflow-y-auto flex-1 min-h-0 space-y-3">
          <OrderBillingFields
            order={order}
            submitting={submitting}
            hidePrice={isAssign}
            duration={billing.duration}
            onDurationChange={billing.setDuration}
            gstPercent={billing.gstPercent}
            priceExGst={billing.priceExGst}
            priceIncGst={billing.priceIncGst}
            errors={billing.errors}
            taxName={billing.taxName}
            taxError={billing.taxError}
            onGstPercentChange={billing.handleGstPercentChange}
            onPriceExGstChange={billing.handlePriceExGstChange}
            onPriceIncGstChange={billing.handlePriceIncGstChange}
          />

          <div className="space-y-1.5">
            <div className="text-xs font-semibold text-gray-600">
              {canEditStaff ? 'Assign Staff' : 'Staff Hours'}
            </div>
            <p className="text-[11px] text-gray-400 leading-relaxed">
              {canEditStaff
                ? 'Changing the hours or the staff list splits the order total equally across the people selected below.'
                : 'Each assigned person receives an equal share of the total hours.'}
            </p>
            {canEditStaff ? (
              loading ? (
                <div className="py-8 text-center">
                  <i className="ri-loader-4-line animate-spin text-green-500 text-xl" />
                  <p className="text-gray-400 text-xs mt-1.5">Loading staff…</p>
                </div>
              ) : error ? (
                <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {error}
                </div>
              ) : members.length === 0 ? (
                <p className="text-center text-gray-400 text-xs py-8">No available staff</p>
              ) : (
                members.map((member) => {
                  const selected = selectedIds.includes(member.id);
                  const online = member.isOnline || member.status === 'working';
                  const avatar =
                    member.avatar?.trim()
                    || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=e8f5e9&color=1b5e20`;
                  return (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => toggle(member.id)}
                      className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md border text-left cursor-pointer transition-colors ${
                        selected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="relative flex-shrink-0 w-8 h-8">
                        <img
                          src={avatar}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover object-top"
                          onError={(e) => {
                            e.currentTarget.src = DEFAULT_AVATAR;
                          }}
                        />
                        <span
                          className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border-2 border-white ${
                            online ? 'bg-green-400' : 'bg-gray-300'
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-800 truncate">{member.name}</div>
                        <div className="text-[11px] text-gray-400 truncate">
                          {online ? 'Online' : 'Offline'}
                          {selected ? ` · ${formatSplitHours(hourSplit[member.id] ?? 0)}` : ''}
                        </div>
                      </div>
                      <div
                        className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 ${
                          selected ? 'bg-green-600 border-green-600' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {selected ? <i className="ri-check-line text-white text-[10px]" /> : null}
                      </div>
                    </button>
                  );
                })
              )
            ) : selectedStaff.length === 0 ? (
              <p className="text-center text-gray-400 text-xs py-6">No assigned staff</p>
            ) : (
              selectedStaff.map((person) => (
                <div
                  key={person.operatorId}
                  className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2"
                >
                  <span className="text-xs font-medium text-gray-800 truncate">{person.name}</span>
                  <span className="text-xs font-semibold text-gray-800 tabular-nums flex-shrink-0">
                    {formatSplitHours(hourSplit[person.operatorId] ?? 0)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="p-3 pt-3 border-t border-gray-100 flex-shrink-0 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 h-9 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium cursor-pointer disabled:opacity-60"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => {
              if (isAssign) {
                const hours = billing.validateHours();
                if (hours == null || selectedIds.length === 0) return;
                onConfirm(selectedIds, {
                  serviceDuration: hours,
                  gstRateDecimal: 0,
                  totalPrice: order.totalPrice ?? 0,
                  totalPriceWithGst: order.totalPriceWithGst ?? order.totalPrice ?? 0,
                }, selectedStaff);
                return;
              }
              const nextBilling = billing.validate();
              if (!nextBilling) return;
              onConfirm(canEditStaff ? selectedIds : undefined, nextBilling, selectedStaff);
            }}
            disabled={submitting || (isAssign ? selectedIds.length === 0 : !billing.gstPercent || (canEditStaff && selectedIds.length === 0))}
            className="flex-1 h-9 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-medium cursor-pointer disabled:opacity-60 flex items-center justify-center gap-1.5"
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin text-sm" />
                {isAssign ? 'Assigning…' : 'Saving…'}
              </>
            ) : (
              isAssign ? 'Assign' : 'Confirm'
            )}
          </button>
        </div>
      </div>
    </>
  );
}
